import React, { useCallback, useEffect, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';
import { getCroppedHeadBlobUrl } from './cropImage';

const stopStream = (stream) => {
  stream?.getTracks?.().forEach((t) => t.stop());
};

/**
 * Modal: live camera → capture frame → circular crop (pan/zoom) → apply head image.
 */
export default function HeadPhotoModal({ open, onClose, onApply, busy }) {
  const [isApplying, setIsApplying] = useState(false);
  const disableUi = busy || isApplying;
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const cropPixelsRef = useRef(null);

  const [step, setStep] = useState('camera'); // 'camera' | 'crop'
  const [cameraError, setCameraError] = useState(null);
  const [streamReady, setStreamReady] = useState(false);
  const [captureSrc, setCaptureSrc] = useState(null);

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [cropPixelsReady, setCropPixelsReady] = useState(false);

  const clearVideo = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const resetInternal = useCallback(() => {
    stopStream(streamRef.current);
    streamRef.current = null;
    clearVideo();
    setStreamReady(false);
    setCameraError(null);
    setCaptureSrc(null);
    setStep('camera');
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    cropPixelsRef.current = null;
    setCropPixelsReady(false);
  }, [clearVideo]);

  useEffect(() => {
    if (!open) {
      resetInternal();
    }
  }, [open, resetInternal]);

  useEffect(() => {
    return () => {
      const video = videoRef.current;
      stopStream(streamRef.current);
      streamRef.current = null;
      if (video) video.srcObject = null;
    };
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    setStreamReady(false);
    stopStream(streamRef.current);
    streamRef.current = null;
    clearVideo();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setStreamReady(true);
    } catch (e) {
      setStreamReady(false);
      setCameraError(
        e?.name === 'NotAllowedError'
          ? 'Camera access was blocked. Allow the camera in your browser settings for this site, then try again.'
          : e?.name === 'NotFoundError'
            ? 'No camera was found on this device.'
            : e?.name === 'NotReadableError'
              ? 'The camera is already in use or could not be opened. Close other apps using the camera and try again.'
              : e?.name === 'SecurityError' || e?.name === 'NotSupportedError'
                ? 'The camera needs a secure connection (HTTPS) or localhost. Open this site over HTTPS and try again.'
                : 'Could not open the camera. Try another browser or check that this page uses HTTPS.'
      );
    }
  }, [clearVideo]);

  const captureFrame = useCallback(() => {
    const video = videoRef.current;
    if (!video || video.readyState < 2) return;

    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) return;

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, w, h);
    stopStream(streamRef.current);
    streamRef.current = null;
    setStreamReady(false);
    clearVideo();

    setCaptureSrc(canvas.toDataURL('image/png'));
    setCropPixelsReady(false);
    setStep('crop');
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  }, [clearVideo]);

  const onCropComplete = useCallback((_area, areaPixels) => {
    cropPixelsRef.current = areaPixels;
    setCropPixelsReady(!!areaPixels?.width);
  }, []);

  const applyCrop = useCallback(async () => {
    const pixels = cropPixelsRef.current;
    if (!captureSrc || !pixels) return;
    setIsApplying(true);
    try {
      const url = await getCroppedHeadBlobUrl(captureSrc, pixels);
      onApply(url);
      onClose();
      resetInternal();
    } catch (err) {
      console.error(err);
    } finally {
      setIsApplying(false);
    }
  }, [captureSrc, onApply, onClose, resetInternal]);

  const retake = useCallback(() => {
    setCaptureSrc(null);
    setStep('camera');
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    cropPixelsRef.current = null;
    setCropPixelsReady(false);
    startCamera();
  }, [startCamera]);

  if (!open) return null;

  return (
    <div
      className="pcg-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="head-photo-modal-title"
    >
      <div className="pcg-modal-panel">
        <div className="pcg-modal-header">
          <h2 id="head-photo-modal-title" className="pcg-modal-title">
            {step === 'camera' ? 'Take your headshot' : 'Crop to your head'}
          </h2>
          <button type="button" onClick={onClose} className="pcg-modal-close" aria-label="Close">
            ×
          </button>
        </div>

        <div className="pcg-modal-body">
          {step === 'camera' ? (
            <div className="pcg-modal-stack">
              <p className="pcg-modal-text">
                Allow the camera when prompted, center your face, then capture. You will crop to a circle next.
              </p>
              <div className="pcg-video-box">
                <video ref={videoRef} className="pcg-video" playsInline muted />
              </div>
              {cameraError ? <p className="pcg-modal-error">{cameraError}</p> : null}
              <div className="pcg-modal-actions">
                <button type="button" onClick={startCamera} disabled={disableUi} className="pcg-btn-teal">
                  {streamReady ? 'Restart camera' : 'Open camera'}
                </button>
                <button
                  type="button"
                  onClick={captureFrame}
                  disabled={disableUi || !!cameraError || !streamReady}
                  className="pcg-btn-amber"
                >
                  Capture photo
                </button>
              </div>
            </div>
          ) : (
            <div className="pcg-modal-stack">
              <p className="pcg-modal-text">
                Drag to position your head inside the circle. Pinch or use the zoom slider to fit.
              </p>
              <div className="pcg-crop-wrap">
                <Cropper
                  image={captureSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  cropShape="round"
                  showGrid={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              </div>
              <div className="pcg-modal-stack">
                <label htmlFor="head-crop-zoom" className="pcg-zoom-label">
                  Zoom
                </label>
                <input
                  id="head-crop-zoom"
                  type="range"
                  min={1}
                  max={3}
                  step={0.01}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="pcg-zoom-range"
                />
              </div>
              <div className="pcg-modal-actions">
                <button
                  type="button"
                  onClick={applyCrop}
                  disabled={disableUi || !cropPixelsReady}
                  className="pcg-btn-amber"
                >
                  {isApplying ? 'Saving…' : 'Use cropped head'}
                </button>
                <button type="button" onClick={retake} disabled={disableUi} className="pcg-btn-outline">
                  Retake photo
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
